import { motion } from "framer-motion";
import { Sun, ArrowDown, BadgeCheck, PiggyBank } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SOLAR_STATS } from "@/lib/siteContent";
import { useContent, resolveImg, isLocalImg } from "@/lib/content";
import { Manifesto } from "@/components/Manifesto";
import { QuoteForm } from "@/components/QuoteForm";
import { Testimonials } from "@/components/Testimonials";
import { Galeria } from "@/components/Galeria";

const EASE = [0.16, 1, 0.3, 1];

export default function EnergiaSolarPage() {
  useSeo(
    "Energia Solar na Bahia | Reduza sua Conta de Luz em até 95% — HB Solar",
    "Energia solar fotovoltaica em Jequié e toda a Bahia: projeto, instalação e homologação completos. 580+ projetos instalados. Simule sua economia grátis."
  );
  const { textos, imagens } = useContent();

  return (
    <>
      <section data-testid="solar-hero" className="relative grain overflow-hidden bg-[#0B192C] pt-[76px]">
        <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-amber-500/15 blur-[140px]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300"
            >
              <Sun className="h-3.5 w-3.5" />
              HB Solar — Energia solar fotovoltaica
            </motion.p>
            <motion.h1
              data-testid="solar-hero-title"
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-white"
            >
              {textos.solarTitulo}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
            >
              {textos.solarSubtitulo}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <a
                data-testid="solar-hero-simulate-button"
                href="#orcamento"
                className="inline-flex items-center gap-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-extrabold px-7 py-4 text-base transition-colors duration-300 shadow-[0_0_36px_rgba(245,158,11,0.35)]"
              >
                Simular minha economia
                <ArrowDown className="h-5 w-5" />
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.55 }}
              className="mt-10 flex flex-wrap gap-x-10 gap-y-4"
            >
              {[
                [SOLAR_STATS.projetos, "Projetos instalados", PiggyBank],
                [SOLAR_STATS.economia, "De economia na conta", Sun],
                [SOLAR_STATS.garantia, "Garantia dos painéis", BadgeCheck],
              ].map(([v, l, Icon]) => (
                <div key={l} className="flex items-center gap-3">
                  <Icon className="h-6 w-6 text-amber-400" />
                  <div>
                    <p className="font-display text-xl font-extrabold text-white">{v}</p>
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{l}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.figure
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: EASE }}
            className="relative rounded-3xl overflow-hidden border border-white/15 shadow-[0_40px_80px_rgba(0,0,0,0.45)]"
          >
            <img
              src={resolveImg(imagens.solarHero)}
              alt="Energia solar fotovoltaica HB Solar"
              className={`h-[420px] lg:h-[500px] w-full ${
                isLocalImg(imagens.solarHero) && !imagens.solarHero.includes("images.unsplash.com")
                  ? "object-contain bg-white p-6"
                  : "object-cover"
              }`}
              loading="eager"
            />
          </motion.figure>
        </div>
      </section>

      <Manifesto />
      <QuoteForm />
      <Testimonials />
      <Galeria />
    </>
  );
}
